import { API_BASE } from './env';
import { hasConsent } from './consent';
import {
  buildClickPayload,
  buildConversionPayload,
  buildImpressionPayload,
  devWarn,
  validConversion,
  validPage,
  validPosition,
  validQueryId,
  validSku
} from './events';
import { IdentityStore } from './identity';
import { ResultObserver } from './observer';
import { Transport } from './transport';
import type {
  ConversionOrder,
  ImpressionContext,
  InitOptions,
  ObserveOptions,
  SearchParams,
  VidisapeTracker
} from './types';

interface RuntimeConfig {
  merchantId: string;
  baseUrl: string;
  consent?: boolean;
}

class Tracker implements VidisapeTracker {
  private consent = false;
  private runtimeConsent: boolean | undefined;
  private merchantId: string | undefined;
  private baseUrl: string | undefined;
  private queryId: string | undefined;
  private readonly identityStore = new IdentityStore();
  private transport: Transport | undefined;
  private observer: ResultObserver | undefined;
  private readonly impressed = new Set<string>();
  private readonly inFlightConversions = new Set<string>();

  init(options: InitOptions): void {
    const config = normalizeInit(options);
    if (!config) {
      devWarn('invalid init options');
      return;
    }

    const nextConsent = hasConsent(config.consent ?? this.runtimeConsent);
    if (config.consent !== undefined) this.runtimeConsent = config.consent;

    if (this.consent) {
      if (nextConsent) this.transport?.flush();
      this.destroyRuntime();
    } else {
      this.destroyRuntime();
    }

    this.merchantId = config.merchantId;
    this.baseUrl = config.baseUrl;
    this.consent = nextConsent;
    if (nextConsent) this.bootRuntime();
  }

  setConsent(value: boolean): void {
    if (typeof value !== 'boolean') {
      devWarn('setConsent requires a boolean');
      return;
    }
    this.runtimeConsent = value;
    const newConsent = hasConsent(value);
    this.consent = newConsent;
    if (!newConsent) {
      this.destroyRuntime();
      return;
    }
    if (this.merchantId && !this.transport) {
      this.bootRuntime();
    }
  }

  attachQuery(queryId: string): void {
    if (!this.ready()) return;
    if (!validQueryId(queryId)) {
      devWarn('attachQuery requires a non-empty queryId');
      return;
    }
    this.queryId = queryId.trim();
  }

  getSearchParams(): SearchParams {
    if (!this.ready()) return { session_id: '', anonymous_user_id: '' };
    return this.identityStore.getSearchParams();
  }

  observeResults(selector: string, options?: ObserveOptions): void {
    if (!this.ready()) return;
    if (!validQueryId(selector)) {
      devWarn('observeResults requires a selector');
      return;
    }
    const normalizedOptions = normalizeObserveOptions(options);
    if (!normalizedOptions) {
      devWarn('invalid observeResults options');
      return;
    }
    if (!this.observer?.observe(selector.trim(), normalizedOptions)) {
      devWarn('observeResults received an invalid CSS selector');
    }
  }

  trackImpression(sku: string, position: number, page?: number): void {
    if (!this.ready()) return;
    if (!this.queryId) {
      devWarn('trackImpression requires attachQuery first');
      return;
    }
    if (!validSku(sku) || !validPosition(position) || !validPage(page)) {
      devWarn('invalid impression payload');
      return;
    }
    const key = `${this.queryId}:${page || 1}:${sku}`;
    if (this.impressed.has(key)) return;
    const identity = this.identityStore.current();
    if (!identity) return;
    this.impressed.add(key);
    const context: ImpressionContext =
      identity.user_id_hash === undefined
        ? {
            query_id: this.queryId,
            session_id: identity.session_id,
            anonymous_user_id: identity.anonymous_user_id
          }
        : {
            query_id: this.queryId,
            session_id: identity.session_id,
            anonymous_user_id: identity.anonymous_user_id,
            user_id_hash: identity.user_id_hash
          };
    const item = page === undefined ? { sku, position } : { sku, position, page };
    this.transport?.enqueueImpression(item, context);
  }

  trackClick(sku: string, position: number, page?: number): void {
    if (!this.ready()) return;
    if (!this.queryId) {
      devWarn('trackClick requires attachQuery first');
      return;
    }
    const identity = this.identityStore.current();
    if (!identity || !validSku(sku) || !validPosition(position) || !validPage(page)) {
      devWarn('invalid click payload');
      return;
    }
    this.transport?.sendClick(buildClickPayload(this.queryId, identity, sku, position, page));
  }

  trackConversion(order: ConversionOrder): void {
    if (!this.ready()) return;
    const identity = this.identityStore.current();
    const normalized = validConversion(order);
    if (!identity || !normalized) {
      devWarn('invalid conversion payload');
      return;
    }
    if (this.identityStore.hasSeenOrder(normalized.order_id) || this.inFlightConversions.has(normalized.order_id)) {
      devWarn('duplicate conversion dropped');
      return;
    }
    const transport = this.transport;
    if (!transport) return;
    this.inFlightConversions.add(normalized.order_id);
    void transport
      .sendConversion(buildConversionPayload(identity, normalized))
      .then((success) => {
        if (success && this.identityStore.current()?.session_id === identity.session_id) {
          this.identityStore.markOrderSeen(normalized.order_id);
        }
      })
      .catch(() => undefined)
      .finally(() => this.inFlightConversions.delete(normalized.order_id));
  }

  identify(userIdHash: string): void {
    if (!this.ready()) return;
    if (!validQueryId(userIdHash)) {
      devWarn('identify requires a non-empty hash');
      return;
    }
    this.identityStore.identify(userIdHash);
  }

  private ready(): boolean {
    if (!this.consent || !this.merchantId || !this.identityStore.current()) return false;
    if (!this.ensureEffectiveConsent()) return false;
    this.identityStore.load();
    return true;
  }

  private makeImpressionPayload(context: ImpressionContext, items: Parameters<typeof buildImpressionPayload>[2]) {
    return buildImpressionPayload(context.query_id, context, items);
  }

  private bootRuntime(): void {
    this.identityStore.load();
    this.transport = new Transport(
      this.merchantId!,
      this.baseUrl!,
      (context, items) => this.makeImpressionPayload(context, items),
      () => this.ensureEffectiveConsent()
    );
    this.observer = new ResultObserver({
      impression: (sku, position, page) => this.trackImpression(sku, position, page),
      click: (sku, position, page) => this.trackClick(sku, position, page)
    });
    const ssrQueryId = readSsrQueryId();
    if (ssrQueryId) this.attachQuery(ssrQueryId);
  }

  private destroyRuntime(): void {
    this.transport?.destroy();
    this.observer?.disconnect();
    this.transport = undefined;
    this.observer = undefined;
    this.queryId = undefined;
    this.impressed.clear();
    this.identityStore.clearRuntime();
  }

  private ensureEffectiveConsent(): boolean {
    if (this.consent && hasConsent(this.runtimeConsent)) return true;
    if (this.consent) {
      this.consent = false;
      this.destroyRuntime();
    }
    return false;
  }
}

function normalizeInit(value: unknown): RuntimeConfig | undefined {
  if (!isRecord(value) || !validQueryId(value.merchantId)) return undefined;
  if (value.apiBase !== undefined && !validQueryId(value.apiBase)) return undefined;
  if (value.consent !== undefined && typeof value.consent !== 'boolean') return undefined;

  const config: RuntimeConfig = {
    merchantId: value.merchantId.trim(),
    baseUrl: value.apiBase === undefined ? API_BASE : value.apiBase.trim().replace(/\/+$/, '')
  };
  if (value.consent !== undefined) config.consent = value.consent;
  return config;
}

function normalizeObserveOptions(value: unknown): ObserveOptions | undefined {
  if (value === undefined) return {};
  if (!isRecord(value)) return undefined;
  const fields = ['skuAttr', 'positionAttr', 'pageAttr'] as const;
  for (const field of fields) {
    if (value[field] !== undefined && !validQueryId(value[field])) return undefined;
  }
  const options: ObserveOptions = {};
  for (const field of fields) {
    const attr = value[field];
    if (typeof attr === 'string') options[field] = attr.trim();
  }
  return options;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readSsrQueryId(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  return document.querySelector<HTMLMetaElement>('meta[name="vidisape-query-id"]')?.content || undefined;
}

const vidisape = new Tracker();

declare global {
  interface Window {
    vidisape: VidisapeTracker;
  }
}

if (typeof window !== 'undefined') {
  window.vidisape = vidisape;
}

export type { ConversionOrder, InitOptions, ObserveOptions, SearchParams, VidisapeTracker } from './types';
export { vidisape };
export default vidisape;
